import React from 'react';
import { ColumnDirective, ColumnsDirective, GridComponent, Inject, Page, Sort, Filter, ExcelExport, PdfExport, FilterType } from '@syncfusion/ej2-react-grids';
import { Box, Typography, Stack } from '@mui/material';
import { WriteoffWorklistView } from '../../../../../services/WriteoffService/types/writeoffWorklistTypes';
import { SearchBar, EmptyState } from '../shared';
import { useWorklistData, useSearchFilter, useWriteoffNavigation } from './hooks';
import {
  patientNameTemplate,
  accountNumberTemplate,
  writeoffStatusTemplate,
  serviceDateTemplate,
  writeoffStartDateTemplate,
  payerTemplate,
} from './templates';
import { gridContainerStyles } from './styles';

// Search fields for worklist
const SEARCH_FIELDS: (keyof WriteoffWorklistView | string)[] = [
  'lastName',
  'firstName',
  'accountNumber',
  'caseId',
  'writeoffStatus',
  'payerName',
];

// Currency template
const amountTemplate = (rowData: WriteoffWorklistView) => (
  <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right' }}>
    ${rowData.amount?.toFixed(2) || '0.00'}
  </Typography>
);

const WriteoffWorklistGrid: React.FC = () => {
  const { worklistItems, isLoading, error, company, center, type } = useWorklistData();
  const { handleRowSelected } = useWriteoffNavigation(company, center);
  const { searchTerm, setSearchTerm, clearSearch, filteredData, hasSearch } = useSearchFilter({
    data: worklistItems,
    searchFields: SEARCH_FIELDS,
  });

  // Grid settings
  const pageSettings = { pageSize: 25, pageSizes: [10, 25, 50, 100] };
  const filterSettings = { type: 'Excel' as FilterType };
  const sortSettings = { allowUnsort: false };

  if (isLoading) {
    return <EmptyState type="loading" message="Loading writeoff worklist..." />;
  }

  if (error) {
    return <EmptyState type="error" message="Error loading writeoff worklist. Please try again." />;
  }

  if (worklistItems.length === 0) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onClear={clearSearch}
            placeholder="Search by patient, account, writeoff ID, status, reason..."
          />
        </Box>
        <EmptyState
          type="no-data"
          message="No writeoffs found"
          subMessage={`No writeoffs available for ${type} in ${center}`}
        />
      </Box>
    );
  }

  if (filteredData.length === 0 && hasSearch) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onClear={clearSearch}
            placeholder="Search by patient, account, writeoff ID, status, reason..."
          />
        </Box>
        <EmptyState
          type="no-results"
          message="No writeoffs match your search"
          subMessage={`Try adjusting your search term: "${searchTerm}"`}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onClear={clearSearch}
            placeholder="Search by patient, account, case ID, status, payer..."
          />
        </Stack>
      </Box>

      {hasSearch && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Showing {filteredData.length} of {worklistItems.length} writeoffs
            {searchTerm && ` matching "${searchTerm}"`}
          </Typography>
        </Box>
      )}

      <Box sx={gridContainerStyles}>
        <GridComponent
          dataSource={filteredData}
          allowPaging={true}
          allowSorting={true}
          allowFiltering={true}
          allowExcelExport={true}
          allowPdfExport={true}
          pageSettings={pageSettings}
          filterSettings={filterSettings}
          sortSettings={sortSettings}
          height="500px"
          rowSelected={handleRowSelected}
        >
          <ColumnsDirective>
            <ColumnDirective
              field="patient"
              headerText="Patient"
              width="150"
              textAlign="Left"
              template={patientNameTemplate}
            />
            <ColumnDirective
              field="accountNumber"
              headerText="Account"
              width="120"
              textAlign="Left"
              template={accountNumberTemplate}
            />
            <ColumnDirective
              field="amount"
              headerText="Amount"
              width="100"
              textAlign="Right"
              template={amountTemplate}
            />
            <ColumnDirective
              field="writeoffStatus"
              headerText="Status"
              width="120"
              textAlign="Center"
              template={writeoffStatusTemplate}
            />
            <ColumnDirective
              field="serviceDate"
              headerText="Service Date"
              width="120"
              textAlign="Center"
              template={serviceDateTemplate}
            />
            <ColumnDirective
              field="writeoffStartDate"
              headerText="Start Date"
              width="120"
              textAlign="Center"
              template={writeoffStartDateTemplate}
            />
            <ColumnDirective
              field="payerName"
              headerText="Payer"
              width="150"
              textAlign="Left"
              template={payerTemplate}
            />
          </ColumnsDirective>
          <Inject services={[Page, Sort, Filter, ExcelExport, PdfExport]} />
        </GridComponent>
      </Box>
    </Box>
  );
};

export default WriteoffWorklistGrid;
